import { dispatchEvent, findElements, toArray } from "../lib/utils";
import { fetchDataSource } from '../lib/mapkick';
import { isMobileApp } from "../helpers";

var attribution = '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>';

function redirectToCadastralPortal(coords) {
  const x = Math.round(coords[1]);
  const y = Math.round(coords[0]);

  const url = `https://nahlizenidokn.cuzk.cz/MapaIdentifikace.aspx?l=KN&x=-${x}&y=-${y}`;

  window.open(url, "KnVypis");
  event.stopPropagation();
}

export default class extends ApplicationController {
  static targets = ["embeddable", "modal", "markerToggle"];

  connect() {
    if (window.cachedMaps == null) window.cachedMaps = {};
    if (window.coordsFromSuggest == null) window.coordsFromSuggest = {};

    this.service = this.createService();

    if (this.mapConfig.inputable) this.initTogglers();

    if (!this.initVisible) return;

    if (this.service) {
      this.service.initialize(() => this.loadScript());
    }
  }

  disconnect() {
    window.cachedMaps = null;
    window.coordsFromSuggest = null;
  }

  embed(event) {
    const modalElement = $("#modal-with-map");
    modalElement.toggleClass("map-modal");
    modalElement.modal("toggle");

    const element = this.element.querySelector(
      `#${event.currentTarget.dataset.mapkickTarget}`
    );

    if (this.scriptLoaded) this.service.create(this.element, element);
  }

  displayMarker(event) {
    const element = this.element.querySelector(
      `#${event.currentTarget.dataset.mapkickTarget}`
    );
    if (this.scriptLoaded) {
      this.service.displayMarker(this.element, element);
      this.toggleMarkers(this.element, event.target.dataset.toggleType);
    }
  }

  removeMarker(event) {
    const element = this.element.querySelector(
      `#${event.target.dataset.mapkickTarget}`
    );
    if (this.scriptLoaded) {
      this.service.removeMarker(this.element, element);
      this.toggleMarkers(this.element, event.target.dataset.toggleType);
    }
  }

  // Private

  createService() {
    return new MapService(this);
  }

  loadScript() {
    this.init.bind(this)();
  }

  init() {
    if (this.scriptLoaded) {
      this.service.create(this.element, this.embeddableTarget);
    }
  }

  initTogglers() {
    const [latitude, longitude] = this.service.getCoordsFromInput(this.element);

    const hasCoords = longitude !== null && latitude !== null;
    const toggleClass = hasCoords ? "enable" : "disable";

    this.toggleMarkers(this.element, toggleClass);
  }

  toggleMarkers(container, toggleClass) {
    const elements = findElements(container, "[data-role~=map-marker-toggle]");

    toArray(elements).forEach((elem) => {
      elem.classList.toggle(
        "visually-hidden",
        elem.dataset.toggleType === toggleClass
      );
    });
  }

  get initVisible() {
    return this.toBoolean(this.data.get("initVisible"));
  }

  get mapConfig() {
    return JSON.parse(this.data.get("config"));
  }

  get geometryConfig() {
    return JSON.parse(this.data.get("geometries"));
  }

  get layersToggleable() {
    return this.toBoolean(this.data.get("layersToggleable"));
  }

  get layerToggleText() {
    return this.data.get("layerToggleText");
  }

  get points() {
    return JSON.parse(this.data.get("points"));
  }

  get seznamApiKey() {
    return this.data.get("seznamApiKey");
  }

  get translationKeys() {
    const rawStr = this.data.get("translationKeys");
    try {
      return JSON.parse(rawStr);
    } catch (error) {
      return {};
    }
  }

  get mapId() {
    return this.data.get("id");
  }

  get scriptLoaded() {
    return true;
  }

  toBoolean(value) {
    return value === "true";
  }
}

class MapService {
  constructor(ctx) {
    this.container = ctx.element;
    this.config = ctx.mapConfig;
    this.geometries = ctx.geometryConfig;
    this.layers = {};
    this.overlayLayers = {};
    this.points = ctx.points;
    this._ctx = ctx;
    this.inputMarkerRefs = {};
  }

  initialize(callback) {
    callback();
  }

  create = (container, elem) => {
    const uuid = `${container.id}-${elem.id}`;
    if (!window.cachedMaps) return;
    let m = window.cachedMaps[uuid];
    let coordsFromSuggest = window.coordsFromSuggest;

    const [latitude, longitude] = this.getCoordsFromInput(container);
    const [rawCoords, useFallbackCoords] = this.getCoords(container);
    const coords = {
      lat: rawCoords.lat,
      lng: rawCoords.lon,
    };

    const {
      autosuggest,
      inputable,
      layers_toggleable: layersToggleable,
      marker_layer: { enable: enableMarker, draggable, ...markerOptions },
    } = this.config;

    let hasCoords = !inputable || (longitude && latitude);

    if (m == null) {
      m = L.map(elem, {
        doubleClickZoom: true,
        dragging: true,
        zoomControl: false,
      }).setView(coords, useFallbackCoords ? 9 : 16);
      L.control.zoom({
        position: 'topright',
      }).addTo(m);
      L.control.scale({imperial: false}).addTo(m);

      window.cachedMaps[uuid] = m;

      this.layers[uuid] = {};
      this.overlayLayers[uuid] = {};
      this.layers[uuid][this._ctx.translationKeys.orto] = L.tileLayer('https://api.mapy.cz/v1/maptiles/aerial/256/{z}/{x}/{y}?apikey=' + this._ctx.seznamApiKey, {
        attribution,
      });
      this.layers[uuid][this._ctx.translationKeys.winter] = L.tileLayer('https://api.mapy.cz/v1/maptiles/winter/256/{z}/{x}/{y}?apikey=' + this._ctx.seznamApiKey, {
        attribution,
      });
      this.layers[uuid][this._ctx.translationKeys.basic] = L.tileLayer('https://api.mapy.cz/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=' + this._ctx.seznamApiKey, {
        attribution,
      });
      this.layers[uuid][this._ctx.translationKeys.turistic] = L.tileLayer('https://api.mapy.cz/v1/maptiles/outdoor/256/{z}/{x}/{y}?apikey=' + this._ctx.seznamApiKey, {
        attribution,
      });

      this.overlayLayers[uuid]["ground"] = L.tileLayer(`${this.config.tiles_url}/guilds/{z}/{x}/{y}.png`, {
        attribution,
      });
      this.overlayLayers[uuid]["description"] = L.tileLayer('https://api.mapy.cz/v1/maptiles/names-overlay/256/{z}/{x}/{y}?apikey=' + this._ctx.seznamApiKey, {
        attribution,
      });

      this.overlayLayers[uuid]["ground"].addTo(m);
      this.overlayLayers[uuid]["description"].addTo(m);
      this.layers[uuid][this._ctx.translationKeys.orto].addTo(m);

      this.overlayLayers[uuid][this._ctx.translationKeys.forest] = L.tileLayer.wmts("https://apollo.uhul.cz/wmts_islh_lho/WMTService.aspx", {
        layer: 'PORS',
        tilematrixset: 'GoogleMapsCompatibleExt:epsg:3857',
        style: 'default'
      });
      this.overlayLayers[uuid][this._ctx.translationKeys.cadaster] = L.tileLayer.katastr("http://services.cuzk.cz/wms/wms.asp?version=1.3.0&CRS=EPSG:32633&REQUEST=GetMap&FORMAT=image/png&styles=&TRANSPARENT=True&layers=KN,DEF_BUDOVY,DEF_PARCELY", {});

      if (layersToggleable) {
        const layerControl = L.control.layers(this.layers[uuid], this.overlayLayers[uuid], {
          position: 'topleft'
        });
        layerControl.addTo(m);

        setTimeout(function() {
          const label1 = document.querySelector('.leaflet-control-layers-overlays > label:nth-child(1)');
          const label2 = document.querySelector('.leaflet-control-layers-overlays > label:nth-child(2)');
          if (label1) label1.style.display = 'none';
          if (label2) label2.style.display = 'none';

          const label5 = document.querySelector('.leaflet-control-layers-overlays > label:nth-child(5)');
          if (label5) {
            const div = document.createElement('div');
            div.classList.add('leaflet-control-layers-separator');
            label5.parentNode.insertBefore(div, label5);
          }
        }, 200);
      }

      // Display points (markers)
      if (this.points) {
        const allMarkers = (this.points || []).map(({ table: topography_point }) => {
          var x = topography_point.coordinates.table.lon;
          var y = topography_point.coordinates.table.lat;
          var coords = {lat: t, lng: x};
          var markerOpts = {};

          const markerUrl = topography_point.marker_icon ? topography_point.marker_icon : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAEElEQVR42mL4//8/A0CAAQAI/AL+26JNFgAAAABJRU5ErkJggg==';
          const baseSize = 40;
          markerOpts.icon = L.icon({
            iconUrl: markerUrl,
            iconSize: [baseSize, baseSize],
            iconAnchor: [baseSize / 2, baseSize],
            popupAnchor: [0, -baseSize]
          });

          markerOpts.title = markerPoint.name;
          var marker = L.marker(coords, markerOpts);

          if (topography_point.has_custom_label) {
            marker.bindTooltip(topography_point.custom_label.replace(/\\n/g, '<br>'), {
              direction: 'center',
              permanent: true,
            }).openTooltip();
          }

          if (topography_point.popup) {
            marker.bindPopup(topography_point.popup, {minWidth: 430})
          }

          if (m.getZoom() >= 14) {
            marker.addTo(m);
          }

          return m
        });

        m.on('zoomend', () => {
          if (m.getZoom() >= 14) {
            allMarkers.forEach(marker => {
              marker.addTo(m);
            });
          } else {
            allMarkers.forEach(marker => {
              marker.remove();
            });
          }
        });
      }

      let suggest = null;
      if (autosuggest) {
      }

      if (!inputable && layersToggleable) {
        m.on('click', (event) => {
          if (m.getZoom() >= 17 && m.hasLayer(this.overlayLayers[uuid][this._ctx.translationKeys.cadaster])) {
            const converter = new JTSK_Converter();
            const coords = converter.WGS84toJTSK(event.latlng.lat, event.latlng.lng);
            redirectToCadastralPortal(coords);
            return;
          }

          if (this.config.huntings_clickable) {
            const url =
              this.config.huntings_marker_url +
              (this.config.huntings_marker_url?.includes('?') ? '&' : '?') +
              'q[search_for_point][lat]=' + event.latlng.lat +
              '&q[search_for_point][lng]=' + event.latlng.lng;

            const markerProxy = {
              render: () => {}
            };
            fetchDataSource(markerProxy, url).then(() => {
              if (markerProxy.rawData?.length) {
                markerProxy.rawData.sort((a, b) => {
                  return a?.points_count - b?.points_count;
                })
                const markerData = markerProxy.rawData[0];

                const markerOpts = {
                  icon: L.icon({
                    iconUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==',
                    iconSize: [1, 1],
                    iconAnchor: [0, 0],
                    popupAnchor: [0, 0],
                  }),
                };
                const tmpMarker = L.marker(event.latlng, markerOpts);
                tmpMarker.addTo(m);

                tmpMarker.bindPopup(markerData.popup, {minWidth: 430}).openPopup();
              }
            });
          }
        });
      }

      // Hide forest map on small zooms

      window.cachedMaps[uuid] = m;
      if (enableMarker && hasCoords) this.displayMarker(container, elem);
    } else {
      window.cachedMaps[uuid] = m;

      if (hasCoords) {
        this.displayMarker(container, elem);
      } else {
        this.removeMarker(container, elem);
      }
    }

    window.cachedMaps[uuid] = m;

    if (Object.keys(this.geometries).length > 0) {
      const {
        marker_layer: { icon },
      } = this.config;

      this.geometries.forEach((geometry) => {
        const { type, id, data, options } = geometry;
        new Mapkick[type](id, data, m, Object.assign(options, { icon }));
      });
    }
  };

  displayMarker = (container, elem) => {
    const uuid = `${container.id}-${elem.id}`;
    if (!window.cachedMaps) return;
    const map = window.cachedMaps[uuid];
    if (map == null) return;

    const {inputable} = this.config;
    const [rawCoords, useFallbackCoords] = this.getCoords(container);
    const coords = {lat: rawCoords.lat, lng: rawCoords.lon};

    if (inputable) this.setInputCoords(container, coords.lat, coords.lng);
    map.setView(coords, useFallbackCoords ? 9 : 16);

    if (this.inputMarkerRefs[uuid] == null) {
      this.initMarker(container, elem, coords);
    } else {
      this.resetMarker(this.inputMarkerRefs[uuid], coords);
    }
  };

  initMarker = (container, elem, coords) => {
    const uuid = `${container.id}-${elem.id}`;
    if (!window.cachedMaps) return;
    const m = window.cachedMaps[uuid];

    const {
      inputable,
      marker_layer: { enable: enableMarker, draggable, ...markerOptions },
    } = this.config;
    const { id, icon } = markerOptions;

    const markerOpts = {draggable};
    if (icon) {
      const baseSize = 32;
      markerOpts.icon = L.icon({
        iconUrl: icon,
        iconSize: [baseSize, baseSize],
        iconAnchor: [baseSize / 2, baseSize],
        popupAnchor: [0, -baseSize],
      });
    }

    const inputMarker = L.marker(coords, markerOpts);
    inputMarker.addTo(m);
    this.inputMarkerRefs[uuid] = inputMarker;

    if (inputable) {
      m.on('click', (event) => {
        if (inputMarker) {
          inputMarker.setLatLng(event.latlng);
        }
        this.reverseGeocode(event.latlng, this.geocode);
      });
    }

    if (draggable) {
      inputMarker.on('dragstart', this.dragStart);
      inputMarker.on('dragend', this.dragStop);
    }
  };

  removeMarker = (container, elem) => {
    const uuid = `${container.id}-${elem.id}`;
    if (!window.cachedMaps) return;
    const map = window.cachedMaps[uuid];

    if (this.inputMarkerRefs[uuid]) {
      this.inputMarkerRefs[uuid].remove();
      this.inputMarkerRefs[uuid] = null;
    }
    this.resetInputCoords(container);
  };

  resetMarker = (marker, coords) => {
    marker.setLatLng(coords);
  };

  getCoords = (container) => {
    const suggested = window.coordsFromSuggest;
    const [latitude, longitude] = this.getCoordsFromInput(container);

    const useFallbackCoords =
      (longitude || (suggested && suggested["lon"]) || this.config.lon) ==
      null &&
      (latitude || (suggested && suggested["lat"]) || this.config.lat) == null;

    const coords = Object.assign(
      {},
      {
        lon:
          longitude ||
          (suggested && suggested["lon"]) ||
          this.config.lon ||
          this.config.fallback_lon,
        lat:
          latitude ||
          (suggested && suggested["lat"]) ||
          this.config.lat ||
          this.config.fallback_lat,
      }
    );

    return [coords, useFallbackCoords];
  };

  dragStart = () => {};

  dragStop = (event) => {
    this.reverseGeocode(event.target.getLatLng(), this.geocode);
  };

  reverseGeocode = async (coords, callback) => {
    try {
      const response = await fetch(`https://api.mapy.cz/v1/rgeocode/?lon=${coords.lng}&lat=${coords.lat}&apikey=${this._ctx.seznamApiKey}`, {
        mode: 'cors',
      });
      const json = await response.json();
      callback?.(json, coords);
      // console.log('Geocoding result', json);
    } catch (e) {
    }
  };

  geocode = (geocoder, coords) => {
    const { lat_input_id: latitudeId, lon_input_id: longitudeId } = this.config;
    if (geocoder?.items?.length) {
      const results = geocoder.items[0];
      const data = Object.assign({
        latitude: results.position.lat,
        longitude: results.position.lon,
      });

      if (!this.setInputCoords(this.container, coords.lat, coords.lng, true)) return data;
    }
  };

  getCoordsFromInput = (container) => {
    const { lat_input_id: latitudeId, lon_input_id: longitudeId } = this.config;
    if (latitudeId && longitudeId) {
      const latInput =
        container.querySelector(`#${latitudeId}`) ||
        document.querySelector(`#${latitudeId}`);
      const lonInput =
        container.querySelector(`#${longitudeId}`) ||
        document.querySelector(`#${longitudeId}`);

      if (!(latInput && latInput.value) && !(lonInput && lonInput.value)) {
        return [null, null];
      }

      return [latInput.value, lonInput.value];
    } else {
      return [null, null];
    }
  };

  setInputCoords = (container, lat, lon, ignoreGeocoder) => {
    const {
      lat_input_id: latitudeId,
      lon_input_id: longitudeId,
      address_input_id: addressId,
    } = this.config;
    if (!(latitudeId && longitudeId)) return false;

    const latInput =
      container.querySelector(`#${latitudeId}`) ||
      document.querySelector(`#${latitudeId}`);
    const lonInput =
      container.querySelector(`#${longitudeId}`) ||
      document.querySelector(`#${longitudeId}`);

    const addressInput =
      container.querySelector(`#${addressId}`) ||
      document.querySelector(`#${addressId}`);

    if (!(latInput && lonInput)) return false;

    latInput.setAttribute("value", lat ? lat : "");
    lonInput.setAttribute("value", lon ? lon : "");

    dispatchEvent(latInput, "input");
    dispatchEvent(lonInput, "input");

    if (addressInput) {
      if (lat && lon) {
        if (!ignoreGeocoder) {
          this.reverseGeocode({lat, lng: lon}, this.fetchAddress);
        }
      } else {
        addressInput.setAttribute("value", "");
      }
    }
  };

  resetInputCoords(container) {
    this.setInputCoords(container, null, null);
  }

  fetchAddress = (geocoder, coords) => {
    const { address_input_id: addressId } = this.config;
    if (!addressId) return false;

    if (geocoder?.items?.length) {
      const results = geocoder.items[0];
      const address = results.name || "";
      const addressInput = document.querySelector(`#${addressId}`);
      if (addressInput) {
        addressInput.setAttribute("value", address);
      }
    }
  };
}
